import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  ScrollView,
  Platform,
  Keyboard,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";

const PHONE_CONTAINER_MAX_WIDTH = 430;

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean; // ✅ new optional prop
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  label,
  error,
  required,
}: SelectProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <View>
      {label && (
        <Text style={[styles.label, { marginBottom: 4, fontWeight: "600", color: colors.text }]}>
          {label}
        </Text>
      )}
      <TouchableOpacity style={styles.trigger} onPress={() => {
        Keyboard.dismiss();
        setIsOpen(true);
      }}>
        <Text style={[styles.value, !selectedOption && styles.placeholder]}>
          {selectedOption?.label || placeholder}
        </Text>
        <Feather name="chevron-down" size={20} color="white" />
      </TouchableOpacity>

      {error && <Text style={{ color: colors.error, marginTop: 4 }}>{error}</Text>}

      <Modal
        visible={isOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setIsOpen(false)}
      >
        <View style={styles.webModalWrapper}>
          <View style={styles.modal}>
            <View style={styles.content}>
              <View style={styles.headerHandle} />
              <View style={styles.header}>
                <Text style={styles.title}>{placeholder}</Text>
                <TouchableOpacity 
                  onPress={() => setIsOpen(false)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Feather name="x" size={22} color="rgba(255, 255, 255, 0.5)" />
                </TouchableOpacity>
              </View>
              <ScrollView 
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
              >
                {options.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.option,
                      option.value === value && styles.selectedOption,
                    ]}
                    onPress={() => {
                      onValueChange(option.value);
                      setIsOpen(false);
                    }}
                  >
                    <Text
                      style={[
                        styles.optionText,
                        option.value === value && styles.selectedOptionText,
                      ]}
                    >
                      {option.label}
                    </Text>
                    {option.value === value && (
                      <Feather name="check" size={20} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  webModalWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' ? {
      maxWidth: PHONE_CONTAINER_MAX_WIDTH,
      alignSelf: 'center',
      width: '100%',
    } : {}),
  },
  scrollView: {
    paddingBottom: 26,
  },
  trigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: colors.surface,
    borderRadius: 12, // More rounded trigger
    borderWidth: 1,
    borderColor: colors.border,
  },
  value: {
    fontSize: 16,
    color: "white",
    fontWeight: "500",
  },
  placeholder: {
    color: "#FFFFFF",
  },
  modal: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0, 0, 0, 0.75)", // Darker, more premium overlay
  },
  content: {
    backgroundColor: "#1A1A1A", // Match app dark theme
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: "80%",
    paddingBottom: Platform.OS === 'ios' ? 40 : 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 20,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 16,
  },
  headerHandle: {
    width: 40,
    height: 4,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: 2,
    alignSelf: "center",
    marginTop: 8,
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "white",
    letterSpacing: -0.3,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 18,
    paddingHorizontal: 20,
    marginHorizontal: 12,
    marginBottom: 4,
    borderRadius: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.05)",
  },
  selectedOption: {
    backgroundColor: "rgba(0, 168, 107, 0.12)", // Slightly stronger primary tint
  },
  optionText: {
    fontSize: 16,
    color: "rgba(255, 255, 255, 0.9)", // Brighter text
    fontWeight: "500",
    letterSpacing: -0.2,
  },
  selectedOptionText: {
    color: colors.primary,
    fontWeight: "700",
  },
  label: {
    // Added label style here to keep consistent styling if needed
  },
});
